import React, { useState, useEffect, useRef } from 'react';
import { Tab } from '@headlessui/react';
import { useAuth } from '../contexts/AuthContext';
import { trackDashboardLoad } from '../utils/appInsights';
import {
  UsersIcon,
  ClockIcon,
  ExclamationCircleIcon,
  TicketIcon,
  ArrowPathIcon,
  BeakerIcon,
} from '@heroicons/react/24/outline';

// Dashboard tab components
import CustomerWiseDashboard from './dashboard/CustomerWiseDashboard';
import SlaMonitoringDashboard from './dashboard/SlaMonitoringDashboard';
import IncidentDashboard from './dashboard/IncidentDashboard';
import ServiceRequestDashboard from './dashboard/ServiceRequestDashboard';
import ChangeRequestDashboard from './dashboard/ChangeRequestDashboard';
import ProblemDashboard from './dashboard/ProblemDashboard';

// Contact Dashboard
import ContactDashboard from './dashboard/ContactDashboard';

const tabs = [
  { name: 'Customer Wise', icon: UsersIcon, component: CustomerWiseDashboard },
  { name: 'SLA Monitoring', icon: ClockIcon, component: SlaMonitoringDashboard },
  { name: 'Incident', icon: ExclamationCircleIcon, component: IncidentDashboard },
  { name: 'Service Request', icon: TicketIcon, component: ServiceRequestDashboard },
  { name: 'Change Request', icon: ArrowPathIcon, component: ChangeRequestDashboard },
  { name: 'Problem', icon: BeakerIcon, component: ProblemDashboard },
];

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ');
}

const Dashboard: React.FC = () => {
  const { user, isContact } = useAuth();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const hasTrackedLoadRef = useRef(false);

  // Track dashboard load when user opens/clicks on Dashboard
  useEffect(() => {
    if (user && !hasTrackedLoadRef.current && !isContact()) {
      const userName = user.email || user.firstName || user.lastName || 'Unknown';
        const userRoleInfo = {
          firstName: user.firstName,
          lastName: user.lastName,
          role: user.role,
          roleType: user.roleType,
          roleLevel: user.roleLevel,
          userClass: user.class,
          customerId: user.customerId,
          customerName: user.customerName,
          engagementId: user.engagementId,
          engagementName: user.engagementName,
          isServiceDeskUser: user.isServiceDeskUser,
        };
      trackDashboardLoad('Dashboard', userName, {}, userRoleInfo);
      hasTrackedLoadRef.current = true;
    }
  }, [user, isContact]);

  // If user is a contact, show the contact dashboard
  if (isContact()) {
    return <ContactDashboard />;
  }

  return (
    <div className="p-6">
      {/* Welcome section */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          Welcome back, {user?.firstName}!
        </h1>
        <p className="mt-1 text-sm text-gray-600">
          Here's what's happening with your IT services today.
        </p>
      </div>

      {/* Tabbed Dashboard */}
      <Tab.Group selectedIndex={selectedIndex} onChange={setSelectedIndex}>
        <Tab.List className="flex space-x-1 rounded-xl bg-gray-100 p-1 mb-6">
          {tabs.map((tab) => (
            <Tab
              key={tab.name}
              className={({ selected }) =>
                classNames(
                  'w-full rounded-lg py-2.5 text-sm font-medium leading-5 transition-all duration-200',
                  'ring-white ring-opacity-60 ring-offset-2 ring-offset-primary-400 focus:outline-none focus:ring-2',
                  selected
                    ? 'bg-white text-primary-700 shadow'
                    : 'text-gray-600 hover:bg-white/[0.5] hover:text-gray-800'
                )
              }
            >
              <div className="flex items-center justify-center">
                <tab.icon className="h-5 w-5 mr-2" />
                <span className="hidden sm:inline">{tab.name}</span>
              </div>
            </Tab>
          ))}
        </Tab.List>
        <Tab.Panels>
          {tabs.map((tab, idx) => (
            <Tab.Panel
              key={idx}
              className={classNames(
                'rounded-xl bg-white',
                'ring-white ring-opacity-60 ring-offset-2 ring-offset-blue-400 focus:outline-none'
              )}
            >
              <tab.component />
            </Tab.Panel>
          ))}
        </Tab.Panels>
      </Tab.Group>
    </div>
  );
};

export default Dashboard;
